import { Plus } from "lucide-react";

import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  Droppable,
  type DroppableProvided,
  type DropResult,
} from "@hello-pangea/dnd";
import { IgButton } from "@intelligems/ig-theme";
import type {
  ComponentPlacementLocation,
  OnsiteUpsellRuleAuthoring,
} from "@intelligems/ig-types";
import Stack from "@mui/material/Stack";
import {
  collectionOptions,
  tagOptions,
} from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/entityOptions";
import { UpsellRuleCard } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/UpsellRuleCard";
import { useFetchAllCollections } from "@src/graphql/hooks/useFetchAllCollections";
import { useProductTags } from "@src/graphql/hooks/useProductTags";

/**
 * The LEGACY rules editor, shown only to orgs without Agentic Logic — engine
 * orgs pick a rule instead. It owns its collections/tags fetches so flag-on
 * orgs never pay for data only this editor reads.
 */
export const RulesSection = ({
  rules,
  location,
  onChange,
}: {
  rules: OnsiteUpsellRuleAuthoring[];
  location: ComponentPlacementLocation;
  onChange: (next: OnsiteUpsellRuleAuthoring[]) => void;
}) => {
  const collections = collectionOptions(useFetchAllCollections());
  const tags = tagOptions(useProductTags());

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const next = [...rules];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    onChange(next);
  };

  const addRule = () =>
    onChange([
      ...rules,
      { conditions: [], products: { source: "recommendations" } },
    ]);

  return (
    <Stack spacing={2}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="onsite-upsell-rules" direction="vertical">
          {(provided: DroppableProvided) => (
            <Stack
              spacing={2}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {rules.map((rule, index) => (
                <Draggable
                  key={index}
                  draggableId={`rule-${index}`}
                  index={index}
                >
                  {(dragProvided: DraggableProvided) => (
                    <UpsellRuleCard
                      rule={rule}
                      ruleIndex={index}
                      location={location}
                      collections={collections}
                      tags={tags}
                      onChange={(next) =>
                        onChange(rules.map((r, i) => (i === index ? next : r)))
                      }
                      onRemove={() =>
                        onChange(rules.filter((_, i) => i !== index))
                      }
                      showRemove={rules.length > 1}
                      dragProvided={dragProvided}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <IgButton
        variant="outlined"
        color="secondary"
        startIcon={<Plus size={16} />}
        onClick={addRule}
        fullWidth
      >
        Add rule
      </IgButton>
    </Stack>
  );
};
